import { NetworkHealthStatus, STATUS_MESSAGES } from "../../services/network.health.service";
import { S25Xml } from "./s25.xml";
import { S25ObjectUtil } from "./s25.object.util";
import { S25Array } from "./s25.array";
import { isString } from "./index";

export namespace S25Error {
    export function extractAll(s25Resp: any, defaultText: string): string[] {
        let msg: string | undefined = "",
            json: any = null;
        const messages: any[] = [];

        if (isAngularHttpTransportFailure(s25Resp)) {
            return [STATUS_MESSAGES[NetworkHealthStatus.Offline] || defaultText];
        }

        if (!s25Resp) {
            msg = defaultText;
        } else if (typeof s25Resp === "string" && s25Resp.indexOf("</") === -1) {
            //string
            msg = s25Resp;
        } else if (
            typeof s25Resp === "object" &&
            (!s25Resp.data || !s25Resp.data.indexOf || s25Resp.data.indexOf("</") === -1)
        ) {
            //json
            json = s25Resp;
        } else {
            //xml
            json = s25Resp && s25Resp.data && S25Xml.prettyConv(s25Resp.data);
        }

        if (json) {
            let errorData = json;
            if (errorData.data) {
                errorData = errorData.data;
            }

            if (!msg) {
                msg =
                    S25ObjectUtil.propertyGet(errorData, "msg") ||
                    S25ObjectUtil.propertyGet(errorData, "error_msg") ||
                    S25ObjectUtil.propertyGet(errorData, "err_msg") ||
                    S25ObjectUtil.propertyGet(errorData, "message");
            }

            //json.message may be special try/catch error property skipped by propertyGet due to Error not having a toJSON property for serialization
            if (!msg) {
                msg = json.message || defaultText;
            }

            //We've collected all the messsages. Each message may have details lets add them... somehow.

            let details = S25ObjectUtil.propertyGetAllUnique(errorData, "proc_error");
            details = details.length < 1 ? S25ObjectUtil.propertyGetAllUnique(errorData, "procError") : details;
            details = details.length < 1 ? S25ObjectUtil.propertyGetAllUnique(errorData, "error_detail") : details;

            if (msg && details) {
                msg = msg.trim();
                details = S25Array.forceArray(details);
                for (let detail of details) {
                    detail = isString(detail) ? detail.trim() : detail.content && detail.content.trim();
                    const objectName = S25ObjectUtil.propertyGet(errorData, "object_name");
                    if (objectName) {
                        detail = ensurePunctuated(detail) + " Object: " + objectName;
                    }

                    //add punctuation to msg, then append details
                    messages.push(`${ensurePunctuated(msg)} Details: ${detail}`);
                }
            }
        }

        return S25Array.forceArray((messages.length > 0 && messages) || msg || defaultText);
    }

    export function extract(s25Resp: any, defaultText: string): string;
    export function extract(s25Resp: any, defaultText?: string): string | undefined;
    export function extract(s25Resp: any, defaultText?: string): string | undefined {
        if (!s25Resp) {
            return defaultText;
        } else if (typeof s25Resp === "string" && s25Resp.indexOf("</") === -1) {
            return s25Resp || defaultText;
        } else {
            let json: any;
            if (
                typeof s25Resp === "object" &&
                (!s25Resp.data || !s25Resp.data.indexOf || s25Resp.data.indexOf("</") === -1)
            ) {
                //json
                json = s25Resp;
            } else {
                //xml
                json = s25Resp?.data && S25Xml.prettyConv(s25Resp.data);
            }

            if (isAngularHttpTransportFailure(json)) {
                return STATUS_MESSAGES[NetworkHealthStatus.Offline] || defaultText;
            }

            const errorData = json?.data ? json.data : json;

            const msg =
                S25ObjectUtil.propertyGet<string>(errorData, [
                    "msg",
                    "error_msg",
                    "err_msg",
                    "message",
                    "errorMessage",
                    "errorMsg",
                    "errMsg",
                ]) ||
                //json.message may be special try/catch error property skipped by propertyGet due to Error not having a toJSON property for serialization
                (json?.message as string) ||
                defaultText;

            if (!msg) {
                return;
            }

            let details = S25ObjectUtil.propertyGet(errorData, [
                "proc_error",
                "procError",
                "error_detail",
                "errorDetail",
                "errDetail",
            ]);
            if (details) {
                details = isString(details) ? details.trim() : details.content && details.content.trim();

                const objectName = S25ObjectUtil.propertyGet(errorData, ["object_name", "objectName"]);
                if (objectName) {
                    details = ensurePunctuated(details) + " Object: " + objectName;
                }

                //add punctuation to msg, then append details
                return ensurePunctuated(msg.trim()) + " Details: " + details;
            } else {
                return msg;
            }
        }
    }

    function ensurePunctuated(str: string) {
        if ([".", "?", "!", ";", ":"].indexOf(str.substring(str.length - 1)) > -1) {
            return str;
        } else {
            return str + ".";
        }
    }

    //Detecting that a failure is suggesting the user is offline
    function isAngularHttpTransportFailure(err: any): boolean {
        if (!err || typeof err !== "object") return false;

        // Angular HttpErrorResponse-style: status 0 for network/unknown errors
        if (typeof err.status === "number" && err.status === 0) return true;

        // Allow the caller to handle the error if the user appears to be online.
        return false;
    }

    export function show(s25Resp: any, defaultText?: string) {
        s25Resp && console.error(s25Resp);
        if (defaultText && typeof defaultText !== "string") {
            defaultText = "";
        }
        const msg = extract(s25Resp, defaultText);
        msg && alert(msg);
    }
}
